import classNames from 'classnames';
import * as React from 'react';
import { MosaicKey } from 'react-mosaic-component/lib/types';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/ProjectDiagramContextMenu';
import { ProjectDiagramTooltip } from 'src/containers/ProjectDiagram/components/tooltip/ProjectDiagramTooltip';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useTooltip } from 'src/containers/ProjectDiagram/hooks/useTooltipValue';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { EntityDrawerType } from 'src/store/ui/types';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRefCallback } from 'src/utils/hooks/useRefCallback';

type ProjectDiagramContainerProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  id: MosaicKey;
  contextMenu: ContextMenuData | null;
  onContextMenuClose: () => void;
};

export const ProjectDiagramContainer: React.VFC<
  ProjectDiagramContainerProps
> = ({ contextMenu, onContextMenuClose }) => {
  const [tooltip] = useTooltip();
  const diagram = useDiagram();
  const { type: drawerType } = useReduxSelector(entityDrawerSelector);
  const isDrawerOpen = drawerType !== EntityDrawerType.NONE;

  const [divRef, setDivRef] = useRefCallback<HTMLDivElement>(null, () => {
    if (diagram && diagram.div === null) {
      diagram.div = divRef.current;
    }
  });

  React.useEffect(() => {
    return () => {
      if (diagram?.div) {
        diagram.div = null;
      }
    };
  }, [diagram, divRef]);

  return (
    <div
      className="sd-project-diagram-container"
      data-testid={TEST_IDS.projectDiagram.container}
    >
      <div className="sd-project-diagram-container__popovers">
        {tooltip ? (
          <ProjectDiagramTooltip
            isOpen
            rect={tooltip?.rect}
            text={tooltip?.text || ''}
          />
        ) : undefined}
        <ProjectDiagramContextMenu
          contextMenu={contextMenu}
          onClose={onContextMenuClose}
        />
      </div>
      <div
        ref={setDivRef}
        className={classNames('sd-project-diagram-container__diagram', {
          'sd-project-diagram-container__diagram-narrow': isDrawerOpen,
        })}
      />
    </div>
  );
};
